import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import {
  SERIENLEISTUNG_TURNUS,
  SERIENLEISTUNG_TURNUS_LABEL,
  SERIENLEISTUNG_TYPEN,
  SERIENLEISTUNG_TYP_LABEL,
  serienleistungenApi,
  type SerienleistungTurnus,
  type SerienleistungTyp,
} from '../lib/serienleistungen-api';
import { requireAccess } from '../lib/guards';

export const Route = createFileRoute('/serienleistungen/neu')({
  beforeLoad: () => requireAccess('/api/v1/serienleistungen'),
  component: NeueSerienleistungPage,
});

interface KundenListItem {
  id: string;
  kundennummer: string | null;
  firmenname: string;
}

interface ObjektListItem {
  id: string;
  bezeichnung: string;
  adresseStrasse: string | null;
  adressePlz: string | null;
  adresseOrt: string | null;
  kundeId: string;
}

interface SubunternehmerListItem {
  id: string;
  firmenname: string;
  ansprechpartner: string | null;
}

export function NeueSerienleistungPage(): JSX.Element {
  const navigate = useNavigate();
  const [kundeId, setKundeId] = useState('');
  const [objektId, setObjektId] = useState('');
  const [bezeichnung, setBezeichnung] = useState('');
  const [leistungstyp, setLeistungstyp] = useState<SerienleistungTyp>('reinigung_treppenhaus');
  const [turnus, setTurnus] = useState<SerienleistungTurnus>('monatlich');
  const [dueDate, setDueDate] = useState('');
  const [subId, setSubId] = useState('');
  const [notizen, setNotizen] = useState('');

  const kundenQ = useQuery({
    queryKey: ['kunden', 'list'],
    queryFn: () => api<{ items: KundenListItem[] }>('/api/v1/kunden'),
  });

  /**
   * Objekt-Liste pro gewähltem Kunde nachladen — wenn kein Kunde gewählt
   * ist, leere Liste rendern und Disabled-Hinweis zeigen. Backend erlaubt
   * nur Objekte des Kunden, andernfalls 422 vom Service.
   */
  const objekteQ = useQuery({
    queryKey: ['objekte', 'list', kundeId],
    queryFn: () =>
      api<{ items: ObjektListItem[] }>(`/api/v1/objekte?kundeId=${encodeURIComponent(kundeId)}`),
    enabled: kundeId.length > 0,
  });

  const subsQ = useQuery({
    queryKey: ['subunternehmer', 'list'],
    queryFn: () => api<{ items: SubunternehmerListItem[] }>('/api/v1/subunternehmer'),
  });

  const create = useMutation({
    mutationFn: () =>
      serienleistungenApi.create({
        kundeId,
        objektId,
        bezeichnung: bezeichnung.trim(),
        leistungstyp,
        turnus,
        naechsteFaelligkeit: dueDate,
        ...(subId.length > 0 ? { subunternehmerId: subId } : {}),
        ...(notizen.trim().length > 0 ? { notizen: notizen.trim() } : {}),
      }),
    onSuccess: ({ item }) => {
      void navigate({ to: '/serienleistungen/$id', params: { id: item.id } });
    },
  });

  const canSubmit =
    kundeId.length > 0 &&
    objektId.length > 0 &&
    bezeichnung.trim().length > 0 &&
    dueDate.length > 0 &&
    !create.isPending;

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="Neue Serienleistung"
        description="Wiederkehrenden Auftrag pro Objekt anlegen — Cron erzeugt täglich die fälligen Aufträge ab Startdatum."
      />

      {create.error ? <ErrorBanner error={create.error as Error} /> : null}

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Kunde</span>
          <Select
            value={kundeId}
            onChange={(e) => {
              setKundeId(e.target.value);
              setObjektId('');
            }}
            required
          >
            <option value="" disabled>
              – Kunde wählen –
            </option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer ? `${k.kundennummer} · ` : ''}
                {k.firmenname}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Objekt {kundeId ? '' : '(zuerst Kunde wählen)'}
          </span>
          <Select
            value={objektId}
            onChange={(e) => setObjektId(e.target.value)}
            disabled={kundeId.length === 0}
            required
          >
            <option value="" disabled>
              – Objekt wählen –
            </option>
            {(objekteQ.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.bezeichnung}
                {o.adresseStrasse ? ` · ${o.adresseStrasse}` : ''}
                {o.adresseOrt ? `, ${o.adresseOrt}` : ''}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Bezeichnung</span>
          <Input
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
            maxLength={200}
            required
            placeholder="z. B. Treppenhausreinigung Hauptstr. 12"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Leistungstyp</span>
          <Select
            value={leistungstyp}
            onChange={(e) => setLeistungstyp(e.target.value as SerienleistungTyp)}
            required
          >
            {SERIENLEISTUNG_TYPEN.map((t) => (
              <option key={t} value={t}>
                {SERIENLEISTUNG_TYP_LABEL[t]}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Turnus</span>
          <Select
            value={turnus}
            onChange={(e) => setTurnus(e.target.value as SerienleistungTurnus)}
            required
          >
            {SERIENLEISTUNG_TURNUS.map((t) => (
              <option key={t} value={t}>
                {SERIENLEISTUNG_TURNUS_LABEL[t]}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Nächste Fälligkeit (erstes Generierungsdatum)
          </span>
          <Input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            required
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Subunternehmer (optional)
          </span>
          <Select value={subId} onChange={(e) => setSubId(e.target.value)}>
            <option value="">– Kein Subunternehmer –</option>
            {(subsQ.data?.items ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.firmenname}
                {s.ansprechpartner ? ` · ${s.ansprechpartner}` : ''}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Notizen (optional)</span>
          <textarea
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            rows={3}
            value={notizen}
            onChange={(e) => setNotizen(e.target.value)}
          />
        </label>

        <div className="flex gap-3">
          <Button type="submit" disabled={!canSubmit}>
            {create.isPending ? 'Speichere…' : 'Anlegen'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void navigate({ to: '/serienleistungen' })}
          >
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
}
